import standardCohortKeys from 'js/utils/cohort-keys';
import {
  keyBy,
  map,
  mapValues,
  mean,
  mergeWith,
  pickBy,
  reduce,
  isObject,
  forEach,
} from 'lodash';
import { titleCase } from 'js/utils/string';
import { getAnswerDomain as getAnswerDomainFromScoring } from 'js/utils/scoring';

function normalizeScore(value, range) {
  const max = Math.max(...range);
  const min = Math.min(...range);
  return value == null ? null : min === max ? 1 : (value - min) / (max - min);
}

export function isScorable(question) {
  return isScoredType(question.type);
}

export function isNumeric(question) {
  return isNumericType(question.type);
}

export function isScoredType(questionType) {
  switch (questionType) {
    case 'long-text':
    case 'unscored-likert':
    case 'multi-select':
    case 'single-select':
      return false;

    default:
      return true;
  }
}

export function isNumericType(questionType) {
  switch (questionType) {
    case 'unscored-likert':
      return true;

    default:
      return isScoredType(questionType);
  }
}

/**
 * Question sets for score charts: sentiment, Likert, and informational Likert.
 * Comments and choice questions are omitted entirely.
 */
export function numericQuestionSets(questionSets) {
  return pickBy(
    mapValues(questionSets ?? {}, (questionSet) => ({
      ...questionSet,
      questions: pickBy(questionSet.questions, isNumeric),
    })),
    (questionSet) => Object.keys(questionSet.questions ?? {}).length > 0
  );
}

export const NA = '__na__';

/**
 * Weight getter for mixed native/rubric collections. If no item has an explicit
 * weight, every item contributes equally (weight 1). Otherwise missing, invalid,
 * or non-positive weights are omitted — matching rubric `included_in_numeric`.
 */
export function makeRollupWeightFn(items) {
  const list = items ?? [];
  const hasExplicitWeights = list.some(
    (item) => item != null && item.weight != null && item.weight !== ''
  );

  return (item) => {
    if (!hasExplicitWeights) {
      return 1;
    }
    const n = Number(item?.weight);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
}

/**
 * Weighted mean of scores. For native assessments, pass `getWeight: () => 1` so
 * behavior matches an unweighted mean over scorable children.
 */
export function rollupWeightedScores(items, { getScore, getWeight }) {
  const weightOf = getWeight || (() => 1);
  let numerator = 0;
  let denominator = 0;
  for (const item of items) {
    const score = getScore(item);
    const w = weightOf(item);
    if (score == null || !w || w <= 0) {
      continue;
    }
    numerator += score * w;
    denominator += w;
  }
  return denominator === 0 ? null : numerator / denominator;
}

/**
 * Weighted median of scores. Equal weights match `median` (including averaging
 * the two middle values when the cumulative weight lands exactly on 50%).
 */
export function rollupWeightedMedian(items, { getScore, getWeight }) {
  const weightOf = getWeight || (() => 1);
  const pairs = [];
  for (const item of items) {
    const score = getScore(item);
    const w = weightOf(item);
    if (score == null || !w || w <= 0) {
      continue;
    }
    pairs.push({ score, weight: w });
  }

  if (pairs.length === 0) {
    return null;
  }

  pairs.sort((a, b) => a.score - b.score);
  const totalWeight = pairs.reduce((sum, pair) => sum + pair.weight, 0);
  const target = totalWeight / 2;
  let cumulative = 0;

  for (let i = 0; i < pairs.length; i++) {
    cumulative += pairs[i].weight;
    if (cumulative >= target) {
      if (cumulative === target && pairs[i + 1]) {
        return (pairs[i].score + pairs[i + 1].score) / 2;
      }
      return pairs[i].score;
    }
  }

  return pairs[pairs.length - 1].score;
}

export function getQuestionSetCategory(questionSet) {
  return questionSet.category || 'uncategorized';
}

export const getAnswerDomain = (answers) =>
  getAnswerDomainFromScoring(answers ?? [], 'value', 2, [0, 0]);

const calculateQuestionSetMetrics = (
  questionSetQuestions,
  participantCohortMap,
  filter,
  assessmentInsertedAt
) => {
  const applyCohortFilter = Object.keys(filter?.cohorts ?? {}).length > 0;

  return keyBy(
    questionSetQuestions.map((q) => {
      const answerDomain = getAnswerDomain(q.answers);
      const [minScore, maxScore] = answerDomain;
      const normalizeScore =
        maxScore - minScore !== 0
          ? (v) => (v == null ? null : (v - minScore) / (maxScore - minScore))
          : (v) => v;
      const scoreMapper = isNumeric(q)
        ? (a) => normalizeScore(+a.value)
        : () => null;
      const castResponseValue = isNumeric(q)
        ? (r) => {
            const castValue = r.is_na ? null : +r.value;
            return isNaN(castValue) ? null : castValue;
          }
        : () => null;
      const answers = (q.answers ?? []).map(({ id, ...a }) => ({
        ...a,
        normalizedScore: scoreMapper(a),
      }));

      const baseMetrics = {
        id: q.id,
        key: q.key,
        name: q.name,
        text: q.text,
        type: q.type,
        lastResponseTime: null,
        assessmentInsertedAt,
        insertedAt: q.inserted_at,
        averageScore: null,
        meanScore: null,
        normalizedScore: null,
        minScore: null,
        maxScore: null,
        normalizedVariance: null,
        responseCount: 0,
        naCount: 0,
        responseTotals: {},
        answerDomain: getAnswerDomain(q.answers),
        answers,
        ...(q.weight != null && q.weight !== '' ? { weight: q.weight } : {}),
      };

      if ((q.responses ?? []).length === 0) {
        return baseMetrics;
      }

      const responseMetrics = (q.responses || []).reduce(
        (acc, response) => {
          const cohortLabels = Array.isArray(
            participantCohortMap[response.participant_id]
          )
            ? participantCohortMap[response.participant_id]
            : [participantCohortMap[response.participant_id]];
          const includeResponse =
            !applyCohortFilter ||
            cohortLabels.some((cohort) => (filter?.cohorts ?? {})[cohort]);
          const responseValue = castResponseValue(response);
          return applyCohortFilter && !includeResponse
            ? acc
            : {
                ...acc,
                lastResponseTime: Math.max(
                  response.inserted_at,
                  acc.lastResponseTime
                ),
                responseCount: response.is_na
                  ? acc.responseCount
                  : acc.responseCount + 1,
                naCount: response.is_na ? acc.naCount + 1 : acc.naCount,
                responseTotals: {
                  ...acc.responseTotals,
                  [response.is_na ? NA : response.value]:
                    (acc.responseTotals[response.is_na ? NA : response.value] ||
                      0) + 1,
                },
                minScore:
                  responseValue != null &&
                  (acc.minScore == null || responseValue < acc.minScore)
                    ? responseValue
                    : acc.minScore,
                maxScore:
                  responseValue != null &&
                  (acc.maxScore == null || responseValue > acc.maxScore)
                    ? responseValue
                    : acc.maxScore,
                totalScore: response.is_na
                  ? acc.totalScore
                  : acc.totalScore + +response.value,
              };
        },
        {
          lastResponseTime: null,
          responseCount: 0,
          naCount: 0,
          totalScore: 0,
          minScore: null,
          maxScore: null,
          responseTotals: q.answers.reduce(
            (acc, answer) => ({
              ...acc,
              [answer.is_na ? NA : answer.value]: 0,
            }),
            {}
          ),
        }
      );

      const meanScore =
        responseMetrics.responseCount === 0 || !isNumeric(q)
          ? null
          : responseMetrics.totalScore / responseMetrics.responseCount;
      const [minAnswerValue, maxAnswerValue] = baseMetrics.answerDomain;

      const normalizedScore = normalizeScore(meanScore);

      const variances = !isNumeric(q)
        ? []
        : (q.responses || [])
            .map((r) => {
              if (r.is_na) {
                return null;
              } else if (minAnswerValue === maxAnswerValue) {
                return 0;
              }

              const val = +r.value;
              const normalizedVal =
                (val - minAnswerValue) / (maxAnswerValue - minAnswerValue);
              return Math.pow(normalizedVal - normalizedScore, 2);
            })
            .filter((x) => x !== null);

      const normalizedVariance =
        variances.length === 0 ? null : mean(variances);

      return {
        ...baseMetrics,
        lastResponseTime: responseMetrics.lastResponseTime,
        responseCount: responseMetrics.responseCount,
        naCount: responseMetrics.naCount,
        responseTotals: responseMetrics.responseTotals,
        averageScore: meanScore,
        meanScore: meanScore,
        minScore: normalizeScore(responseMetrics.minScore),
        maxScore: normalizeScore(responseMetrics.maxScore),
        normalizedScore,
        normalizedVariance: isNaN(normalizedScore) ? null : normalizedVariance,
      };
    }),
    'key'
  );
};

const mergeQuestionData = (currentData, questionData) => {
  return mergeWith(currentData, questionData, (orig, source) => {
    if (source === undefined) {
      return orig;
    } else if (orig === undefined) {
      return source;
    }

    if (
      orig.assessmentInsertedAt > source.assessmentInsertedAt &&
      orig.responseCount + orig.naCount > 0
    ) {
      return orig;
    }

    return source;
  });
};

export const getSummaryMetrics = (assessments, options) => {
  options = {
    filter: null,
    ...options,
  };

  let configCategories = {};

  // find all of the categories
  // find all the question sets
  // find all of the questions
  const dataMap = {
    categories: {},
    questionSets: {},
    assessments: {},
  };

  // for a category, remember all of the question sets it contains
  // for a question set (key), remember the question set it belongs to
  // for a question set (key), remember all of the questions it has ever had
  // for a question set (key), remember the last time it has been asked
  // for a question, remember the last time it has been asked
  // for a question, remember the aggregate score from the last time it was asked
  // TODO: for a question, remember last sets of possible answers (most recent are index 0)

  assessments.forEach((a) => {
    const assessmentCategories = a?.config?.categories ?? {};
    if (assessmentCategories !== undefined) {
      configCategories = {
        ...configCategories,
        ...mapValues(assessmentCategories, () => null),
      };
    }

    const participantCohortMap =
      options.participantCohortsMap ??
      (a.participants ?? []).reduce((acc, p) => {
        acc[p.id] = [p.cohort ?? standardCohortKeys.NULL];
        return acc;
      }, {});

    const assessmentInsertedAt = new Date(a.inserted_at);
    const assessmentMetrics = (dataMap.assessments[a.id] = {
      questionSets: {},
      insertedAt: assessmentInsertedAt,
    });

    (a.question_sets || []).forEach((qs, i) => {
      const questionSetMetrics = {
        lastResponseTime: null, // set later
        assessmentInsertedAt,
        insertedAt: qs.inserted_at,
        normalizedScore: null, // set later
        normalizedVariance: null,
        key: qs.key,
        displayName: qs.name,
        category: getQuestionSetCategory(qs),
        lastRun: qs.inserted_at,
        questions: calculateQuestionSetMetrics(
          qs.questions ?? [],
          participantCohortMap,
          options.filter,
          assessmentInsertedAt
        ),
        ...(qs.weight != null && qs.weight !== '' ? { weight: qs.weight } : {}),
      };
      questionSetMetrics.lastResponseTime = reduce(
        questionSetMetrics.questions,
        (acc, q) => {
          return q.lastResponseTime > acc ? q.lastResponseTime : acc;
        },
        null
      );

      const qsQuestions = Object.values(questionSetMetrics.questions);
      const normalizedQsScore = rollupWeightedScores(qsQuestions, {
        getScore: (q) =>
          isScorable(q) && q.meanScore !== null ? q.normalizedScore : null,
        getWeight: makeRollupWeightFn(qsQuestions.filter(isScorable)),
      });

      questionSetMetrics.normalizedScore = normalizedQsScore;

      const variances = map(questionSetMetrics.questions, (q) =>
        !isScorable(q) || q.normalizedScore === null
          ? null
          : Math.pow(normalizedQsScore - q.normalizedScore, 2)
      ).filter((x) => x !== null);

      questionSetMetrics.normalizedVariance =
        normalizedQsScore === null || variances.length === 0
          ? null
          : mean(variances);

      assessmentMetrics.questionSets[qs.key] = questionSetMetrics;

      // TODO: something weird is going on here (setting this key multiple times)...need to investigate more
      const origQsData = dataMap.questionSets[qs.key] || {
        lastRun: null,
        displayName: qs.name,
        questions: {},
      };

      const questions = mergeQuestionData(
        origQsData.questions,
        questionSetMetrics.questions
      );

      const qsData = (dataMap.questionSets[qs.key] = {
        // lastResponseTime,
        displayName: qs.name,
        key: qs.key,
        category: getQuestionSetCategory(qs),
        ...origQsData,
        lastRun:
          qs.inserted_at > origQsData.lastRun
            ? qs.inserted_at
            : origQsData.lastRun,
        questions,
        normalizedScore: null, // calculate this after everything is compiled
        normalizedVariance: null, // calculate this after everything is compiled
        ...(qs.weight != null && qs.weight !== ''
          ? { weight: qs.weight }
          : origQsData.weight != null && origQsData.weight !== ''
          ? { weight: origQsData.weight }
          : {}),
      });

      dataMap.categories[qsData.category] = {
        ...dataMap.categories[qsData.category],
        [qs.key]: null,
      };
    });

    const assessmentQuestionSets = Object.values(
      assessmentMetrics.questionSets
    );
    assessmentMetrics.normalizedScore = rollupWeightedScores(
      assessmentQuestionSets,
      {
        getScore: (qs) => qs.normalizedScore,
        getWeight: makeRollupWeightFn(assessmentQuestionSets),
      }
    );
  });

  dataMap.categories = pickBy(
    {
      ...mapValues(configCategories, (o, key) => dataMap.categories[key]),
      ...dataMap.categories,
    },
    (o) => o !== undefined
  );

  forEach(dataMap.questionSets, (qs) => {
    const combinedQuestions = Object.values(qs.questions);
    const scoredQuestions = combinedQuestions.filter(isScorable);
    const normalizedScore = rollupWeightedScores(scoredQuestions, {
      getScore: (q) => q.normalizedScore,
      getWeight: makeRollupWeightFn(scoredQuestions),
    });
    qs.normalizedScore = normalizedScore;

    const variances = map(qs.questions, (q) =>
      !isScorable(q) || q.normalizedScore === null
        ? null
        : Math.pow(qs.normalizedScore - q.normalizedScore, 2)
    ).filter((x) => x !== null);
    const normalizedVariance = variances.length === 0 ? null : mean(variances);

    qs.normalizedVariance = normalizedVariance;
  });

  return dataMap;
};

export const generateBaseParticipantMetrics = () => ({
  questionSets: {},
  naCount: 0,
  responseCount: 0,
  meanNormalizedScore: null,
});

export const getParticipantMetrics = (assessments, options) => {
  options = {
    filter: null,
    ...options,
  };

  return assessments.reduce((acc, a) => {
    const rawParticipantMetrics = {};

    a.question_sets.forEach((qs) => {
      qs.questions.forEach((q) => {
        if (isScorable(q)) {
          const answerDomain = getAnswerDomain(q.answers);
          q.responses.forEach((r) => {
            const participantId = r.participant_id;
            const isNa = r.is_na;
            const normalizedScore = isNa
              ? null
              : normalizeScore(r.value, answerDomain);

            const { questionSets } = (rawParticipantMetrics[participantId] =
              rawParticipantMetrics[participantId] ?? { questionSets: {} });
            const { questions } = (questionSets[qs.key] = questionSets[
              qs.key
            ] ?? { questions: {} });
            questions[q.key] = {
              isNa,
              normalizedScore,
              value: r.value,
            };
          });
        } else {
          // TODO: handle non-scorable questions
        }
      });
    });

    const assessment = mapValues(
      rawParticipantMetrics,
      (participantMetrics, participantId) => {
        const questionSets = (participantMetrics.questionSets = mapValues(
          participantMetrics.questionSets,
          (questionSetMetrics, questionSetKey) => {
            const { scoredResponses, naCount } = reduce(
              questionSetMetrics.questions,
              (acc, q) => {
                return {
                  ...acc,
                  scoredResponses:
                    q.normalizedScore === null
                      ? acc.scoredResponses
                      : [...acc.scoredResponses, q.normalizedScore],
                  naCount: q.isNa ? acc.naCount + 1 : acc.naCount,
                };
              },
              { scoredResponses: [], naCount: 0 }
            );

            return {
              ...questionSetMetrics,
              meanNormalizedScore: mean(scoredResponses),
              responseCount: scoredResponses.length,
              naCount: naCount,
            };
          }
        ));

        const { responseCount, naCount, scoredResponses } = reduce(
          questionSets,
          (acc, qs) => {
            return {
              scoredResponses: [...acc.scoredResponses, qs.meanNormalizedScore],
              responseCount: qs.responseCount + acc.responseCount,
              naCount: qs.naCount + acc.naCount,
            };
          },
          {
            scoredResponses: [],
            responseCount: 0,
            naCount: 0,
          }
        );

        return {
          questionSets,
          naCount,
          responseCount,
          meanNormalizedScore: mean(scoredResponses),
        };
      }
    );

    return { ...acc, [a.id]: assessment };
  }, {});
};

function categoryDisplayName(key) {
  return key === 'uncategorized' ? 'Uncategorized' : titleCase(key);
}

export const getCategoryLookups = (assessments, supplementalCategories) => {
  // NOTE: we could do this without the supplemental categories, but we'd have to do more calculations
  const configCategories = assessments
    .map((a) => {
      return a.config?.categories;
    })
    .filter(isObject)
    .reduce((acc, categories) => {
      return { ...acc, ...categories };
    }, {});

  const allCategories = mergeWith(
    configCategories,
    supplementalCategories,
    (obj, _src, key) => {
      if (obj?.display_name) {
        return obj;
      }

      const display_name = categoryDisplayName(key);

      if (obj !== undefined) {
        return { ...obj, display_name };
      }

      return { display_name };
    }
  );

  const categoryMap = new Map(
    map(allCategories, (value, key) => [key, value]).sort(
      (a, b) => a[1]?.ordinal - b[1]?.ordinal
    )
  );

  let ci = 0;
  categoryMap.forEach((value, key) => {
    if (!value.display_name) {
      value.display_name = categoryDisplayName(key);
    }
    value.palette = `palette-cat-${++ci}`;
  });

  return categoryMap;
};
